import {
  Dispatch, SetStateAction, useEffect, useMemo, useState,
} from "react";
import { useSnackbar } from "@kitbird/core";
import { useLocation } from "react-router-dom";
import { useInfiniteQuery, useQuery } from "react-query";
import { json2csv } from "json-2-csv";
import Jspdf from "jspdf";
import autoTable from "jspdf-autotable";
import { dateToFormatedStringDate, toStringDate } from "../../../../utils/common";
import { columnOptions, csvKeys } from "../constants";
import {
  DepartmentItemI, getTrx, getTrxHistory, getUserPoolDepartment, PoolItemI,
} from "../../../../services/trxService";
import useAuth from "../../../../components/Authentication/useAuth";
import { PageType, TabTypes } from "../enums";
import getHasAccessAction from "../../../../components/Authentication/utils";
import { isIGetTrxHistoryData } from "./utils";
import { IOptionItem } from "../../../../typings/common";
import { useDebounce } from "../../../../hooks";
import { getBusinessSegment } from "../../../../services/businessService/businessService";
import CodeTypeOpts, { CommonTrxTypes } from "../../../../enums/common";

interface FilterI {
  status_approval: "CLOSE" | "CANCEL" | "OPEN" | "CREATED",
  status_bon?: {
    label: string,
    value: "CLOSE" | "CANCEL" | "OPEN" | "",
  },
  department?: {
    label: string,
    value: string,
  },
  codeType?: {
    label: string,
    value: string,
  },
  pool?: {
    label: string,
    value: string,
  },
  company?: {
    label: string,
    value: string,
  },
  dates?: Date[],
  search?: string,
  sortBy: string,
  sortType: "ASC" | "DSC" | "DEFAULT",
}

const useTableHook = (
  setLoading: Dispatch<SetStateAction<boolean>>,
) => {
  const { rbacInfo, userInfo, userAccount } = useAuth();
  const isCreator = getHasAccessAction({
    menus: rbacInfo,
    menuTitle: "Flow creator pengajuan bon",
  });
  const isApprover = getHasAccessAction({
    menus: rbacInfo,
    menuTitle: "Flow approver pengajuan bon",
  });

  const paramsPosition = useMemo(() => {
    const { position_info: positionInfo } = userAccount;
    if (!positionInfo || positionInfo.length === 0) {
      return { areas: "", pools: "", codeType: "" };
    }
    const initialCodeType = positionInfo[0]?.code_type || "";
    if (initialCodeType === "O") {
      return { areas: "", pools: "", codeType: "" };
    }

    const { uniqueAreas, uniquePools } = positionInfo.reduce(
      (acc, position) => {
        if (position.area_id) {
          acc.uniqueAreas.add(position.area_id);
        }
        if (position.pool_id) {
          acc.uniquePools.add(position.pool_id);
        }
        return acc;
      },
      { uniqueAreas: new Set(), uniquePools: new Set() },
    );

    return {
      areas: [...uniqueAreas].join(","),
      pools: [...uniquePools].join(","),
      codeType: initialCodeType,
    };
  }, [userAccount.position_info]);

  const snackbar = useSnackbar();
  const location = useLocation();
  const [initDates] = useState<Date[] | undefined>([new Date(), new Date()]);

  const initialTab = useMemo(() => (
    isApprover ? TabTypes.OPEN : TabTypes.CREATED
  ), [isApprover]);

  const [tab, setTab] = useState<TabTypes>(initialTab);

  const [trxParams, setTrxParams] = useState<FilterI>({
    status_bon: { label: "Lihat semua", value: "" },
    status_approval: initialTab,
    sortBy: "trx_date",
    sortType: "DSC",
    dates: initialTab === TabTypes.CREATED ? initDates : undefined,
    company: { label: "Lihat semua", value: "" },
  });
  const [meta, setMeta] = useState({
    page: 1,
    page_size: 10,
    total_items: 0,
    total_pages: 0,
  });
  const [fetchCount, setFetchCount] = useState(0);
  const [pageType, setPageType] = useState<PageType>(PageType.LIST);
  const [columnOpts, setColumnOpts] = useState<IOptionItem[]>(JSON.parse(localStorage.getItem("COLUMN_RECEIPT_SUBMISSION") || JSON.stringify(columnOptions)));
  const [hiddenColumn, setHiddenColumn] = useState<string[]>([]);
  const [searchText, setSearchText] = useState("");

  const [dataAllPool, setDataAllPool] = useState<PoolItemI[]>([]);
  const [dataAllDepartment, setDataAllDepartment] = useState<DepartmentItemI[]>([]);
  const [seeAllDepartmentOpt, setSeeAllDepartmentOpt] = useState<
    { label: string, value: string } | undefined
  >();
  const [currPoolOpts, setCurrPoolOpts] = useState<IOptionItem[]>([]);
  const [currDepartmentOpts, setCurrDepartmentOpts] = useState<IOptionItem[]>([]);
  const [seeAllCompanyOpt] = useState<{ label: string, value: string }>(
    { label: "Lihat semua", value: "" },
  );

  const { data: dataPoolsDepartments, isLoading: isPoolsDepartmentsLoading } = useQuery({
    queryKey: ["get_user_pools_departments", userAccount],
    queryFn: () => getUserPoolDepartment({
      code_type: userAccount.position_info[0].code_type,
      department: userAccount.position_info[0].department,
      job_title: userAccount.position_info[0].job_title,
      user_email: userAccount.employee_email,
    }),
  });

  useEffect(() => {
    setTrxParams((curr) => ({ ...curr, search: searchText }));
  }, [useDebounce(searchText, 300)]);

  useEffect(() => {
    const codeTypeOpt = {
      label: "Pool",
      value: "P",
    };

    setTrxParams((curr) => ({
      ...curr,
      codeType: codeTypeOpt,
      pool: undefined,
      department: seeAllDepartmentOpt,
      company: seeAllCompanyOpt,
    }));
    setSearchText("");
    if (location.pathname.includes("riwayat-pengajuan-bon")) setTab(TabTypes.CREATED);
    else if (isCreator) setTab(TabTypes.CREATED);
    else if (isApprover) setTab(TabTypes.OPEN);
  }, [location.pathname]);

  const isOnRiwayatPage = useMemo(() => location.pathname.includes("riwayat-pengajuan-bon"), [location.pathname]);

  useEffect(() => {
    setTrxParams((prev) => {
      const newParams = { ...prev, status_approval: tab };

      if (tab === TabTypes.OPEN || isOnRiwayatPage) {
        newParams.dates = undefined;
      } else {
        newParams.dates = initDates;
      }

      return newParams;
    });
  }, [tab, isOnRiwayatPage, initDates]);

  useEffect(() => {
    let isAllHidden = true;
    const hiddenCol = columnOpts.map((opt) => {
      let val = "";
      if (!opt.checked) val = opt.value.toString();
      else isAllHidden = false;
      return val;
    });
    if (isAllHidden) {
      setColumnOpts(columnOptions);
      localStorage.setItem("COLUMN_RECEIPT_SUBMISSION", JSON.stringify(columnOptions));
      setHiddenColumn([]);
    } else {
      localStorage.setItem("COLUMN_RECEIPT_SUBMISSION", JSON.stringify(columnOpts));
      setHiddenColumn(hiddenCol);
    }
  }, [columnOpts]);

  useEffect(() => {
    if (dataPoolsDepartments) {
      let pools = [];
      if (dataPoolsDepartments.list_pool.length > 0) {
        pools = dataPoolsDepartments.list_pool.filter((value, i, self) => (
          i === self.findIndex((t) => (
            t.pool_id === value.pool_id
          ))
        ));
      } else {
        pools = userAccount.position_info.filter((value, i, self) => (
          i === self.findIndex((t) => (
            t.pool_id === value.pool_id
          ))
        ));
      }
      setDataAllPool(pools);

      let departments = [];
      if (dataPoolsDepartments.list_department.length > 0) {
        departments = dataPoolsDepartments.list_department;
      } else {
        departments = userAccount.position_info.filter((value, i, self) => (
          i === self.findIndex((t) => (
            t.department === value.department
          ))
        )).map((item) => ({
          department: item.department,
          department_desc: item.department,
          code_type: item.code_type,
        }));
      }
      setDataAllDepartment(departments);
      setSeeAllDepartmentOpt(
        departments.length > 1
          ? { label: "Lihat semua", value: departments.map((item) => item.department).join(",") }
          : undefined,
      );
    }
  }, [dataPoolsDepartments]);

  useEffect(() => {
    if (!isPoolsDepartmentsLoading) {
      let poolOpts: { label: string, value: string }[] = [];
      if (dataAllPool && dataAllPool.length > 0) {
        poolOpts = dataAllPool.map((item) => ({ label: item.pool_name, value: item.pool_id }));
      } else {
        poolOpts = userAccount?.userPools.map((position) => (
          { label: position.poolName, value: position.poolId }
        ));
      }
      setCurrPoolOpts(poolOpts);
      if (poolOpts.length === 1) setTrxParams((curr) => ({ ...curr, pool: poolOpts[0] }));
      else setTrxParams((curr) => ({ ...curr, pool: undefined }));

      let departmentOpts: { label: string, value: string }[] = [];
      if (dataAllDepartment && dataAllDepartment.length > 0) {
        departmentOpts = dataAllDepartment.map(
          (item) => ({ label: item.department, value: item.department }),
        );
      } else {
        departmentOpts = userAccount?.userDepartments.map((department) => (
          { label: department, value: department }
        )) || [];
      }

      setCurrDepartmentOpts(departmentOpts);
      if (departmentOpts.length === 1) {
        setTrxParams((curr) => ({ ...curr, department: departmentOpts[0] }));
      } else setTrxParams((curr) => ({ ...curr, department: seeAllDepartmentOpt }));
    }
  }, [userAccount, dataAllPool, dataAllDepartment]);

  const { data: dataCompanies, isLoading: isCompaniesLoading } = useQuery({
    queryKey: ["get_companies", trxParams.codeType, trxParams.pool],
    enabled: !!trxParams.pool?.value && !!trxParams.codeType?.value,
    // && getValues("pool.value") !== "HO",
    queryFn: () => getBusinessSegment(
      trxParams.codeType?.value as string,
      dataAllPool.find((item) => item.pool_id === (trxParams.pool?.value || ""))?.area_id as string,
      trxParams.pool?.value as string,
    ),
  });

  const fetchList = (pageParam: number, isSetMeta?: boolean) => {
    const startDate = trxParams.dates && trxParams.dates[0];
    const endDate = trxParams.dates && trxParams.dates[1];
    const formatedStartDate = dateToFormatedStringDate(startDate);
    const formatedEndDate = dateToFormatedStringDate(endDate);
    const sortBy = trxParams.sortType === "DEFAULT" ? "trx_date" : trxParams.sortBy;
    const sortType = trxParams.sortType !== "ASC" ? "DESC" : "ASC";

    return isOnRiwayatPage
      ? getTrxHistory({
        status_bon: trxParams.status_bon?.value,
        page_number: JSON.stringify(pageParam),
        page_size: 10,
        search: trxParams.search,
        start_date: formatedStartDate ?? "",
        end_date: formatedEndDate ?? "",
        employee_email: userInfo.email,
        sort_by: sortBy,
        sort_type: sortType,
        department: trxParams.department?.value || "",
        pool_id: trxParams.pool?.value || "",
        company_code: trxParams.company?.value || "",
      }).then((res) => {
        if (isSetMeta && res.metadata) setMeta(res.metadata);
        return res.list_trx_history;
      }).catch(() => [])
      : getTrx({
        status_approval: trxParams.status_approval,
        page_number: JSON.stringify(pageParam),
        page_size: 10,
        search: trxParams.search,
        start_date: formatedStartDate ?? "",
        end_date: formatedEndDate ?? "",
        employee_email: userInfo.email,
        sort_by: sortBy,
        sort_type: sortType,
        pool_id: paramsPosition.pools,
        area_id: paramsPosition.areas,
        code_type: paramsPosition.codeType,
      }).then((res) => {
        if (isSetMeta && res.metadata) setMeta(res.metadata);
        return res.list_transaction;
      }).catch(() => []);
  };

  const transactionQuery = useInfiniteQuery(
    ["transactionList", trxParams, location.pathname],
    async ({ pageParam = 1 }) => (
      (location.pathname.includes("riwayat-pengajuan-bon")
        && (!trxParams.pool || !trxParams.company))
        ? []
        : fetchList(pageParam, true)
    ),
    {
      getNextPageParam: () => (
        meta.page < meta.total_pages
          ? meta.page + 1
          : undefined
      ),
      keepPreviousData: true,
    },
  );

  const generateStatus = (stat: string) => {
    let val = " ";
    switch (stat) {
      case "OPEN":
        val = "Diproses";
        break;
      case "CLOSE":
        val = "Selesai";
        break;
      case "CANCEL":
        val = "Batal";
        break;
      case "TRANSFER":
        val = "Proses transfer";
        break;
      default:
        val = stat;
        break;
    }
    return val;
  };

  const generateCsvRows = async (page: number) => {
    const res = await fetchList(page);
    const row = res.map((item, j) => ({
      no: ((page - 1) * 10 + j + 1).toString(),
      status_trx: generateStatus(item.status_trx),
      trx_id: item.trx_id,
      trx_date: toStringDate(item.trx_date),
      amount: item.amount,
      trx_type_description: (isIGetTrxHistoryData(item))
        ? item.trx_type_description
        : item.trx_type.description,
      trx_text: item.trx_text,
      employee_name: item.employee_name,
      employee_nip: item.employee_nip,
      department: item.department.department,
      job_title: item.job_title,
      code_description: (isIGetTrxHistoryData(item))
        ? item.code_description
        : item.segment.code_description,
      area_name: item.area_name,
      pool_id: item.pool_id,
      company_code: item.company_code,
      company_relation: item.company_relation || " ",
      trx_code: item.trx_code.trx_code,
      common_trx_type: CommonTrxTypes.find((type) => type.value === item.sap_shkzg)?.label,
      trx_desc: item.trx_desc,
      bank_account_name: item.bank_account_name,
      plan_postdate: toStringDate(item.plan_postdate),
    }));
    setFetchCount((curr) => curr + 1);
    return row;
  };

  const handleDownloadCsv = async () => {
    try {
      if (meta.total_pages > 0) {
        setFetchCount(0);
        setLoading(true);
        const allCsvRows = [];

        let abortedPage = "";

        for (let page = 1; page < meta.total_pages + 1; page += 1) {
          // eslint-disable-next-line no-await-in-loop
          const csvRows = await generateCsvRows(page);
          if (csvRows.length > 0) allCsvRows.push(csvRows);
          else {
            abortedPage = `${page} / ${meta.total_pages}`;
            page = meta.total_pages;
          }
        }
        if (!abortedPage) {
          const anchor = document.createElement("a");
          const csv = json2csv(
            allCsvRows.flatMap((row) => row),
            {
              keys: csvKeys.filter((item) => (
                item.sourceKey
                  ? !JSON.stringify(hiddenColumn).includes(item.sourceKey)
                  : true
              )),
            },
          );
          const blob = new Blob([csv], { type: "text/csv; charset=utf-8" });
          const url = URL.createObjectURL(blob);
          anchor.href = url;
          anchor.download = `filtered_riwayat_pengajuan_bon_${new Date().toISOString().split("T")[0]}.csv`;
          anchor.click();
          window.URL.revokeObjectURL(url);
          setFetchCount(0);
          setLoading(false);
        } else {
          snackbar.show(`Gagal download CSV, data table page ${abortedPage} gagal`, "error");
          setFetchCount(0);
          setLoading(false);
        }
      } else {
        snackbar.show("Gagal download CSV, data table kosong", "error");
        setFetchCount(0);
        setLoading(false);
      }
    } catch (error) {
      snackbar.show("Gagal download CSV", "error");
    }
  };

  const handleDownloadPdf = async () => {
    try {
      if (meta.total_pages > 0) {
        setFetchCount(0);
        setLoading(true);
        const allCsvRows = [];
        let abortedPage = "";

        for (let page = 1; page < meta.total_pages + 1; page += 1) {
          // eslint-disable-next-line no-await-in-loop
          const csvRows = await generateCsvRows(page);
          if (csvRows.length > 0) allCsvRows.push(csvRows);
          else {
            abortedPage = `${page} / ${meta.total_pages}`;
            page = meta.total_pages;
          }
        }

        if (!abortedPage) {
          const doc = new Jspdf({
            orientation: "landscape",
            format: [700, 400],
          });
          const displayedColumns = csvKeys
            .filter((item) => (
              item.sourceKey
                ? !JSON.stringify(hiddenColumn).includes(item.sourceKey)
                : true
            ));

          autoTable(doc, {
            head: [displayedColumns.map((key) => key.title)],
            body: allCsvRows.flatMap((row) => row).map((row: any) => {
              const newRow: any = {};
              displayedColumns.forEach((item) => {
                newRow[item.field] = row[item.field];
              });
              return Object.values(newRow);
            }),
          });
          doc.save(`riwayat_pengajuan_bon_${new Date().toISOString().split("T")[0]}.pdf`);
          setFetchCount(0);
          setLoading(false);
        } else {
          snackbar.show(`Gagal download PDF, data table page ${abortedPage} gagal`, "error");
          setFetchCount(0);
          setLoading(false);
        }
      } else {
        snackbar.show("Gagal download PDF, data table kosong", "error");
        setFetchCount(0);
        setLoading(false);
      }
    } catch (error) {
      snackbar.show("Gagal download PDF", "error");
    }
  };

  return {
    isCreator,
    isApprover,
    pageType,
    setPageType,
    trxParams,
    setTrxParams,
    searchText,
    setSearchText,
    dataPoolsDepartments,
    isPoolsDepartmentsLoading,
    meta,
    setMeta,
    fetchCount,
    setFetchCount,
    tab,
    setTab,
    columnOpts,
    setColumnOpts,
    hiddenColumn,
    dataAllDepartment,
    seeAllDepartmentOpt,
    currPoolOpts,
    currDepartmentOpts,
    setCurrDepartmentOpts,
    dataCompanies,
    isCompaniesLoading,

    transactionQuery,
    handleDownloadCsv,
    handleDownloadPdf,
    seeAllCompanyOpt,
  };
};

export default useTableHook;
